import {
    Box, Typography, Button, Tooltip, IconButton, Tabs, Tab, Dialog,
    DialogActions, DialogContent, DialogContentText, DialogTitle, Chip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Cards from "../../ReusableComponents/Cards/Cards";
import SearchBar from "../../ReusableComponents/Search/SearchBar";
import CustomCard from "../../ReusableComponents/CustomCard/CustomCard";
import CustomSnackbar from "../../components/CustomSnackbar";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import api from "../../api/api";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddNotification from "./AddNotification";
import CustomTable from "../../ReusableComponents/Table/CustomTable";
import { ButtonStyles } from "../../Styles/ButtonStyles";
import en_ja from '../../translations/en_ja';

export default function Notifications() {
    const [searchTerm, setSearchTerm] = useState("");
    const [tab, setTab] = useState(0);
    const [notificationData, setNotificationData] = useState([]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");

    const tabOptions = [en_ja["All"], en_ja["Promotional"], en_ja["Transactional"]];
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);

    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [notificationToDelete, setNotificationToDelete] = useState(null);

    const [cardsData, setCardsData] = useState({});

    const getAllCards = () => {
        api.get("/v1/admin/get/notification/stats")
            .then((res) => {
                setCardsData(res?.data?.stats || {})
            })
            .catch((err) => console.error(err));
    };

    useEffect(() => {
        getAllCards()
    }, []);

    const handleTabChange = (event, newValue) => setTab(newValue);

    const filteredByStatus = (row) => {
        const selectedTab = tabOptions[tab];

        // Always include all for "All"
        if (selectedTab === en_ja["All"]) return true;

        // Map Japanese tab labels back to English backend keys
        const tabToEnglish = {
            [en_ja["Promotional"]]: "Promotional",
            [en_ja["Transactional"]]: "Transactional",
        };

        const backendType = tabToEnglish[selectedTab];
        return row.type?.toLowerCase() === backendType?.toLowerCase();
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setSnackbarMessage("");
        setSnackbarType("success");
    };

    const handleAddNotification = () => {
        setEditMode(false);
        setDialogOpen(true);
    };

    const handleEdit = (row) => {
        setSelectedNotification(row);
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleDeleteClick = (row) => {
        setNotificationToDelete(row);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (notificationToDelete) {
            try {
                await api.delete(`/v1/admin/delete/notification/${notificationToDelete.id}`);
                setDeleteConfirmOpen(false);
                setNotificationToDelete(null);
                setSnackbarMessage(en_ja["Notification deleted successfully"]);
                setSnackbarType("success");
                setSnackbarOpen(true);
                getAllNotifications();
            } catch (error) {
                console.error("Error deleting notification:", error);
                setSnackbarMessage(en_ja["Failed to delete notification"]);
                setSnackbarType("error");
                setSnackbarOpen(true);
            }
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmOpen(false);
        setNotificationToDelete(null);
    };

    const getAllNotifications = async () => {
        setLoading(true);
        try {
            const res = await api.get("/v1/admin/get/all-notifications");
            setNotificationData(res?.data?.notifications || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAllNotifications();
    }, []);

    useEffect(() => {
        setRows(notificationData);
    }, [notificationData]);

    const filteredData = notificationData.filter((row) => {
        const term = searchTerm.toLowerCase();
        return (
            (row.title?.toLowerCase().includes(term) ||
                row.type?.toLowerCase().includes(term) ||
                row.status?.toLowerCase().includes(term) ||
                row.recipients?.toString().includes(term) ||
                row.sent_date?.toLowerCase().includes(term)) &&
            filteredByStatus(row)
        );
    });

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedNotification(null);
        setEditMode(false);
    };

    const handleDialogSubmit = () => {
        getAllNotifications();
    };

    const handleSendNotification = async (row) => {
        try {
            const res = await api.post("/v1/admin/send/notification", { notificationId: row.id });
            if (res.status === 200 || res.data === "200") {
                setSnackbarMessage(en_ja["Notification sent successfully"]);
                setSnackbarType("success");
                setSnackbarOpen(true);
                getAllNotifications();
            }
        } catch (error) {
            console.error(error);
            setSnackbarMessage(en_ja["Failed to send notification"]);
            setSnackbarType("error");
            setSnackbarOpen(true);
        }
    };

    const columns = [
        { id: "title", label: en_ja["Title"], sortable: true },
        { id: "type", label: en_ja["Type"], sortable: true },
        {
            id: "status",
            label: en_ja["Status"],
            sortable: true,
            render: (row) => {
                let bgColor =
                    row.status === "Sent" ? "green" :
                        row.status === "Scheduled" ? "blue" :
                            row.status === "Draft" ? "gray" : "orange";

                return (
                    <Chip
                        label={row.status}
                        size="small"
                        sx={{
                            backgroundColor: bgColor,
                            color: "white",
                            fontWeight: 600,
                            fontSize: "10px",
                            textTransform: "uppercase",
                        }}
                    />
                );
            },
        },
        { id: "recipients", label: en_ja["Recipients"], sortable: true },
        {
            id: "sent_date",
            label: en_ja["Sent Date"],
            sortable: true,
            render: (row) => {
                if (!row.sent_date) return "";
                const date = new Date(row.sent_date);
                const formattedDate = `${String(date.getDate()).padStart(2, "0")}/${String(
                    date.getMonth() + 1
                ).padStart(2, "0")}/${date.getFullYear()}`;
                return formattedDate;
            },
        },
        {
            id: "actions",
            label: en_ja["Actions"],
            render: (row) => (
                <Box display="flex" justifyContent="start" gap={1}>
                    <Tooltip title={en_ja["Edit Notification"]} arrow>
                        <IconButton aria-label="edit" size="small" onClick={() => handleEdit(row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={en_ja["Deactivate Notification"]} arrow>
                        <IconButton
                            aria-label="delete"
                            size="small"
                            onClick={() => handleDeleteClick(row)}
                            sx={{
                                color: "error.main",
                                "&:hover": { backgroundColor: "error.light", color: "error.dark" },
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
        {
            id: "",
            label: "",
            render: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {row.status === "Draft" && (
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={() => handleSendNotification(row)}
                        >
                            {en_ja["Send"]}
                        </Button>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ mt: 2 }}>
            <CustomSnackbar
                isOpen={snackbarOpen}
                type={snackbarType}
                message={snackbarMessage}
                onClose={handleSnackbarClose}
            />

            <Box
                sx={{
                    display: "grid",
                    gap: 3,
                    width: "100%",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    maxWidth: "800px",
                }}
            >
                <Cards
                    title={en_ja["Total Notifications"]}
                    value={cardsData?.totalNotifications ?? 0}
                    icon={<NotificationsActiveIcon sx={{ color: "white", fontSize: 24 }} />}
                />
                <Cards
                    title={en_ja["Sent This Month"]}
                    value={cardsData?.notificationsSentThisMonth ?? 0}
                    icon={<CheckCircleIcon sx={{ color: "white", fontSize: 24 }} />}
                />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", my: 3 }}>
                <Button sx={ButtonStyles} onClick={handleAddNotification}>
                    {en_ja["Add New Notification"]}
                </Button>
            </Box>

            <CustomCard
                boxSx={{ flexDirection: "row", justifyContent: "space-between", flexWrap: "wrap" }}
                innerBoxSx={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", width: "100%" }}
            >
                <Box>
                    <SearchBar value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </Box>

             
        <Tabs value={tab}>
          <Tab
            label={
              <Box sx={{ fontWeight: 700 }}>
                {en_ja["All"] || "All"}
                {" "}
                {!loading && <Box component="span" sx={{ color: "red" }}>
                  ({cardsData.totalNotifications})
                </Box>}
              </Box>
            }
          />
        </Tabs>

            </CustomCard>

            <CustomTable
                columns={columns}
                rows={filteredData}
                searchTerm={searchTerm}
                loading={loading}
                setSearchTerm={setSearchTerm}
            />

            <AddNotification
                dialogOpen={dialogOpen}
                onClose={handleDialogClose}
                editMode={editMode}
                notificationData={selectedNotification}
                onSubmit={handleDialogSubmit}
                setSnackbarType={setSnackbarType}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarMessage={setSnackbarMessage}
            />

            <Dialog
                open={deleteConfirmOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="delete-dialog-title" sx={{ color: "error.main" }}>
                    {en_ja["Confirm Delete"]}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {en_ja["Are you sure you want to delete the notification"]}{" "}
                        <strong>{notificationToDelete?.title}</strong>? {en_ja["This action cannot be undone."]}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={handleDeleteCancel} variant="outlined" color="primary">
                        {en_ja["Cancel"]}
                    </Button>
                    <Button onClick={handleDeleteConfirm} variant="contained" color="error" autoFocus>
                        {en_ja["Delete"]}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
